import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Search, Link, BarChart2, ChevronDown, ChevronUp, X, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import ImportQuantitiesButton from './ImportQuantitiesButton';
import { can } from '../../services/session';
import AddMappingModal from './AddMappingModal';
import { PLATFORM_CONFIG } from './platformConfig';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://store.comparehomeupgrades.com/api';

const PLATFORM_COLORS = {
  shopify: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', dot: 'bg-green-500' },
  ebay:    { bg: 'bg-blue-100',  text: 'text-blue-700',  border: 'border-blue-300',  dot: 'bg-blue-500'  },
  amazon:  { bg: 'bg-orange-100',text: 'text-orange-700',border: 'border-orange-300',dot: 'bg-orange-500'},
};

const fmt = (v) => `£${(parseFloat(v) || 0).toFixed(2)}`;
const fmtN = (v) => parseInt(v) || 0;

// ── Create Master Product Modal ────────────────────────────────
// Creates the master product AND its mapping for the current platform in one step —
// necessary because this page only ever shows products already mapped to `platform`
// (see the filter below), so a product created without a mapping here would
// immediately vanish with no way to give it one.
function CreateProductModal({ platform, cfg, stores, onClose, onSaved }) {
  const [form, setForm] = useState({ master_sku: '', product_name: '', cost_price: '' });
  const [sku, setSku] = useState('');
  const [variationSku, setVariationSku] = useState('');
  const [storeId, setStoreId] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.master_sku || !form.product_name) {
      toast.error('Master SKU and product name are required');
      return;
    }
    if (!sku.trim()) { toast.error(`Please enter ${cfg.skuLabel}`); return; }
    if (cfg.hasStores && !storeId) { toast.error(`Please select a ${cfg.label} store`); return; }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/sku-mapping/master-products`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, cost_price: parseFloat(form.cost_price) || 0 })
      });
      const data = await res.json();
      if (!data.success) { toast.error(data.error); return; }

      const mapRes = await fetch(`${API_BASE_URL}/sku-mapping/master-products/${data.id}/mappings`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          platform,
          platform_sku: sku.trim(),
          variation_sku: variationSku.trim() || undefined,
          store_id: cfg.hasStores ? storeId : undefined,
        })
      });
      const mapData = await mapRes.json();
      if (!mapData.success) {
        toast.error(`Product created, but the ${cfg.label} mapping failed: ${mapData.error}`);
        onSaved(); onClose();
        return;
      }

      toast.success('Master product created!');
      onSaved(); onClose();
    } catch { toast.error('Failed to create product'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold text-gray-800">New Master Product</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Master SKU *</label>
            <input
              type="text"
              value={form.master_sku}
              onChange={e => setForm(f => ({ ...f, master_sku: e.target.value }))}
              placeholder="e.g. LAB-COAT-S"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
            <input
              type="text"
              value={form.product_name}
              onChange={e => setForm(f => ({ ...f, product_name: e.target.value }))}
              placeholder="e.g. Lab Coat Small"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cost Price (£)</label>
            <input
              type="number"
              step="0.01"
              value={form.cost_price}
              onChange={e => setForm(f => ({ ...f, cost_price: e.target.value }))}
              placeholder="0.00"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div className="pt-2 border-t border-gray-200">
            <p className="text-xs font-medium text-gray-500 mb-3 mt-3 uppercase tracking-wide">{cfg.label} mapping</p>

            {cfg.hasStores && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{cfg.label} Store *</label>
                <select
                  value={storeId}
                  onChange={e => setStoreId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Select a store...</option>
                  {(stores || []).map(s => (
                    <option key={s.id} value={s.id}>{s.store_name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">{cfg.skuLabel} *</label>
              <input
                type="text"
                value={sku}
                onChange={e => setSku(e.target.value)}
                placeholder={cfg.skuPlaceholder}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {cfg.variationLabel && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{cfg.variationLabel}</label>
                <input
                  type="text"
                  value={variationSku}
                  onChange={e => setVariationSku(e.target.value)}
                  placeholder={cfg.variationPlaceholder}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
                />
                <p className="text-xs text-gray-400 mt-1">{cfg.variationHint}</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium"
          >
            {saving ? 'Creating...' : 'Create Product'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────
export function SkuMapping({ platform = 'ebay' }) {
  const cfg = PLATFORM_CONFIG[platform] || PLATFORM_CONFIG.ebay;
  const colors = PLATFORM_COLORS[platform] || PLATFORM_COLORS.ebay;

  const [tab, setTab] = useState('mapping'); // 'mapping' | 'report'
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [addMappingFor, setAddMappingFor] = useState(null);
  const [stores, setStores] = useState([]);
  const [pushingId, setPushingId] = useState(null);
  const [pushingMappingId, setPushingMappingId] = useState(null);

  // Report state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/sku-mapping/master-products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setProducts(data.products);
    } catch { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  useEffect(() => {
    if (!cfg.storeApi) { setStores([]); return; }
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}${cfg.storeApi}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.success) setStores(d.stores); })
      .catch(() => {});
  }, [cfg.storeApi]);

  const handleQuantityChange = async (productId, quantity) => {
    setProducts(ps => ps.map(p => p.id === productId ? { ...p, quantity } : p));
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/sku-mapping/master-products/${productId}/quantity`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity })
      });
    } catch { toast.error('Failed to save quantity'); }
  };

  // Patch the live_quantity badge for one mapping in place, so it updates immediately
  // after a push instead of waiting for the next full product list refresh.
  const updateMappingLiveQuantity = (productId, storeId, newQuantity) => {
    setProducts(ps => ps.map(p => {
      if (p.id !== productId) return p;
      return {
        ...p,
        mappings: p.mappings.map(m => (m.platform === platform && m.store_id === storeId) ? { ...m, live_quantity: newQuantity } : m)
      };
    }));
  };

  const handlePushEbayQuantity = async (productId, quantity) => {
    setPushingId(productId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/sku-mapping/master-products/${productId}/${cfg.pushPath}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity })
      });
      const data = await res.json();
      if (!data.success) { toast.error(data.error || 'Push failed'); return; }
      const succeeded = data.results.filter(r => r.success).length;
      const skipped = data.results.filter(r => r.skipped).length;
      const failedRows = data.results.filter(r => !r.success && !r.skipped);
      if (data.results.length === 0) {
        toast.info(`Nothing mapped to ${cfg.label} for this product yet`);
      } else {
        toast.success(`Updated ${succeeded} ${cfg.hasStores ? 'store' : 'listing'}${succeeded !== 1 ? 's' : ''} to ${data.quantity}${skipped ? `, ${skipped} skipped` : ''}${failedRows.length ? `, ${failedRows.length} failed` : ''}`);
        data.results.filter(r => r.success).forEach(r => {
          if (r.store_id != null) updateMappingLiveQuantity(productId, r.store_id, data.quantity);
        });
        failedRows.forEach(r => {
          toast.error(`${r.store_name || r.sku || 'Listing'} failed: ${r.error}`, { autoClose: 10000 });
        });
        if (failedRows.length) console.error(`Push-to-${cfg.label} failures:`, failedRows);
      }
    } catch { toast.error(`Failed to push quantity to ${cfg.label}`); }
    finally { setPushingId(null); }
  };

  const handlePushSingleStore = async (productId, storeId, storeLabel, quantity) => {
    setPushingMappingId(`${productId}-${storeId}`);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/sku-mapping/master-products/${productId}/${cfg.pushPath}/${storeId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity })
      });
      const data = await res.json();
      if (!data.success) { toast.error(data.error || 'Push failed'); return; }
      const r = data.result;
      if (r.skipped) toast.info(`${storeLabel}: nothing mapped, nothing to push`);
      else if (r.success) {
        toast.success(`${storeLabel} updated to ${data.quantity}`);
        updateMappingLiveQuantity(productId, storeId, data.quantity);
      }
      else toast.error(`${storeLabel} failed: ${r.error}`, { autoClose: 10000 });
    } catch { toast.error(`Failed to push quantity to ${cfg.label}`); }
    finally { setPushingMappingId(null); }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this master product and all its mappings?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/sku-mapping/master-products/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted');
      fetchProducts();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDeleteMapping = async (mappingId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/sku-mapping/mappings/${mappingId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mapping removed');
      fetchProducts();
    } catch { toast.error('Failed to remove mapping'); }
  };

  const generateReport = async () => {
    if (!startDate || !endDate) { toast.error('Please select a date range'); return; }
    setReportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_BASE_URL}/sku-mapping/unified-report?start_date=${startDate}&end_date=${endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success) {
        setReportData(data);
        toast.success(`Report generated — ${data.data.length} products found`);
      } else {
        toast.error(data.error);
      }
    } catch { toast.error('Failed to generate report'); }
    finally { setReportLoading(false); }
  };

  // Only ever show products that already have a mapping for THIS platform — this page is
  // for managing existing platform mappings, not browsing the whole catalog. A brand-new
  // mapping is created (together with its master product, if needed) via "New Master Product".
  const platformProducts = products.filter(p => (p.mappings || []).some(m => m.platform === platform));
  const filteredProducts = platformProducts.filter(p =>
    p.product_name.toLowerCase().includes(search.toLowerCase()) ||
    p.master_sku.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">
            <span className={`inline-block w-3 h-3 rounded-full mr-2 align-middle ${colors.dot}`} />
            {cfg.label} SKU Mapping
          </h1>
          <p className="text-gray-500 mt-1">
            {cfg.hasStores
              ? `Bind each master product to its ${cfg.skuLabel} across your ${cfg.label} stores`
              : `Bind each master product to its ${cfg.label} SKU`}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setTab('mapping')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all ${
            tab === 'mapping'
              ? 'bg-orange-600 text-white shadow-lg'
              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
          }`}
        >
          <Link size={18} /> SKU Mappings
          <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${tab === 'mapping' ? 'bg-orange-500' : 'bg-gray-200 text-gray-600'}`}>
            {platformProducts.length}
          </span>
        </button>
        <button
          onClick={() => setTab('report')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all ${
            tab === 'report'
              ? 'bg-orange-600 text-white shadow-lg'
              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
          }`}
        >
          <BarChart2 size={18} /> Unified Report
        </button>
      </div>

      {/* ── MAPPING TAB ── */}
      {tab === 'mapping' && (
        <>
          <div className="flex gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search master products..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
            {can('sku_mapping', 'edit') && <>
            <ImportQuantitiesButton platform={platform} stores={stores} label={`Import ${cfg.label} Quantities`} onDone={fetchProducts} />
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium shadow-lg"
            >
              <Plus size={18} /> New Master Product
            </button>
            </>}
          </div>

          {loading ? (
            <div className="text-center py-16 text-gray-400">
              <RefreshCw className="animate-spin mx-auto mb-3" size={28} />
              Loading...
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-12 text-center">
              <Link className="mx-auto mb-4 text-gray-300" size={48} />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No master products yet</h3>
              <p className="text-gray-400 mb-6">Create your first master product to start mapping it to {cfg.label}.</p>
              <button
                onClick={() => setShowCreate(true)}
                className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
              >
                <Plus className="inline mr-2" size={18} />Create First Product
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredProducts.map(product => {
                const isExpanded = expanded[product.id];
                const myMappings = (product.mappings || []).filter(m => m.platform === platform);
                const otherPlatforms = ['shopify', 'ebay', 'amazon']
                  .filter(pl => pl !== platform && (product.mappings || []).some(m => m.platform === pl));

                return (
                  <div key={product.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    {/* Header row */}
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setExpanded(e => ({ ...e, [product.id]: !e[product.id] }))}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-mono text-sm font-bold px-2 py-0.5 rounded ${colors.text} ${colors.bg}`}>
                              {product.master_sku}
                            </span>
                            <span className="font-medium text-gray-800 truncate">{product.product_name}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-gray-500">Cost: £{parseFloat(product.cost_price || 0).toFixed(2)}</span>
                            <span className="text-xs text-gray-400">•</span>
                            <span className={`text-xs ${myMappings.length > 0 ? colors.text : 'text-gray-400'}`}>
                              {myMappings.length} {cfg.hasStores ? 'store' : 'SKU'}{myMappings.length !== 1 ? 's' : ''} mapped
                            </span>
                            {otherPlatforms.length > 0 && (
                              <span className="text-xs text-gray-400">
                                • also on {otherPlatforms.map(p => PLATFORM_CONFIG[p].label).join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <input
                          type="number"
                          min="0"
                          value={product.quantity ?? 0}
                          onClick={e => e.stopPropagation()}
                          onChange={e => handleQuantityChange(product.id, parseInt(e.target.value) || 0)}
                          className="w-20 px-2 py-1.5 text-sm border border-gray-300 rounded-lg text-center"
                          title="Master quantity"
                        />
                        {cfg.hasPush && (
                          <button
                            onClick={e => { e.stopPropagation(); handlePushEbayQuantity(product.id, product.quantity ?? 0); }}
                            disabled={pushingId === product.id}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 font-medium disabled:opacity-50"
                            title={`Push quantity to all mapped ${cfg.label} stores`}
                          >
                            <RefreshCw size={14} className={pushingId === product.id ? 'animate-spin' : ''} />
                            {pushingId === product.id ? 'Pushing...' : `Push to ${cfg.label}`}
                          </button>
                        )}
                        <button
                          onClick={e => { e.stopPropagation(); setAddMappingFor(product); }}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 font-medium"
                        >
                          <Plus size={14} /> Add SKU
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); handleDeleteProduct(product.id); }}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 size={16} />
                        </button>
                        {isExpanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                      </div>
                    </div>

                    {/* Expanded mappings */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 p-4 bg-gray-50">
                        {myMappings.length === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-4">
                            Not mapped to {cfg.label} yet. Click "Add SKU" to link {cfg.hasStores ? `a ${cfg.skuLabel}` : 'the SKU'}.
                          </p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {myMappings.map(m => {
                              const mappingKey = `${product.id}-${m.store_id}`;
                              const isPushingThis = pushingMappingId === mappingKey;
                              return (
                              <div key={m.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-200">
                                <div className="min-w-0">
                                  {cfg.hasStores && (
                                    <span className="block text-xs text-gray-400 truncate">
                                      {m.store_name || `Store #${m.store_id}`}
                                    </span>
                                  )}
                                  <span className="font-mono text-sm text-gray-800 break-all">{m.platform_sku}</span>
                                  {m.variation_sku && (
                                    <span className="block font-mono text-xs text-gray-400 break-all">{m.variation_sku}</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 ml-2 shrink-0">
                                  {m.live_quantity != null ? (
                                    <span
                                      title={`Current live quantity on ${cfg.label} — red: out of stock, yellow: 10 or fewer left, green: more than 10`}
                                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                        Number(m.live_quantity) === 0
                                          ? 'bg-red-100 text-red-700'
                                          : m.live_quantity <= 10
                                          ? 'bg-yellow-100 text-yellow-700'
                                          : 'bg-green-100 text-green-700'
                                      }`}
                                    >
                                      {m.live_quantity}
                                    </span>
                                  ) : (
                                    <span title="Not synced yet — run a Products sync for this store" className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-400">
                                      —
                                    </span>
                                  )}
                                  {cfg.perStorePush && (
                                    <button
                                      onClick={() => handlePushSingleStore(product.id, m.store_id, m.store_name || `Store #${m.store_id}`, product.quantity ?? 0)}
                                      disabled={isPushingThis}
                                      title={`Push quantity to ${m.store_name || 'this store'} only`}
                                      className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg disabled:opacity-50"
                                    >
                                      <RefreshCw size={13} className={isPushingThis ? 'animate-spin' : ''} />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDeleteMapping(m.id)}
                                    className="text-gray-300 hover:text-red-500"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── REPORT TAB ── */}
      {tab === 'report' && (
        <>
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Unified SKU Report</h2>
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <button
                onClick={generateReport}
                disabled={reportLoading}
                className="flex items-center gap-2 px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium"
              >
                <BarChart2 size={18} />
                {reportLoading ? 'Generating...' : 'Generate Report'}
              </button>
            </div>
          </div>

          {reportData && (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Total Orders', value: fmtN(reportData.summary.total_orders), color: 'text-orange-600' },
                  { label: 'Total Units',  value: fmtN(reportData.summary.total_units),  color: 'text-blue-600'   },
                  { label: 'Total Revenue',value: fmt(reportData.summary.total_revenue), color: 'text-green-600'  },
                  { label: 'Total Profit', value: fmt(reportData.summary.total_profit),  color: 'text-purple-600' },
                ].map(card => (
                  <div key={card.label} className="bg-white rounded-xl shadow-sm p-5">
                    <p className="text-gray-500 text-sm mb-1">{card.label}</p>
                    <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
                  </div>
                ))}
              </div>

              {/* Report table */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                      <tr>
                        <th className="text-left py-3 px-4 text-gray-600 font-semibold">Product</th>
                        {/* Shopify */}
                        <th className="text-right py-3 px-3 font-semibold">
                          <span className="flex items-center justify-end gap-1">
                            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                            <span className="text-green-700">Shopify</span>
                          </span>
                        </th>
                        <th className="text-right py-3 px-3 text-green-700 font-semibold">Revenue</th>
                        {/* eBay */}
                        <th className="text-right py-3 px-3 font-semibold">
                          <span className="flex items-center justify-end gap-1">
                            <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                            <span className="text-blue-700">eBay</span>
                          </span>
                        </th>
                        <th className="text-right py-3 px-3 text-blue-700 font-semibold">Revenue</th>
                        {/* Amazon */}
                        <th className="text-right py-3 px-3 font-semibold">
                          <span className="flex items-center justify-end gap-1">
                            <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" />
                            <span className="text-orange-700">Amazon</span>
                          </span>
                        </th>
                        <th className="text-right py-3 px-3 text-orange-700 font-semibold">Revenue</th>
                        {/* Totals */}
                        <th className="text-right py-3 px-3 text-gray-600 font-semibold border-l border-gray-200">Total Units</th>
                        <th className="text-right py-3 px-3 text-gray-600 font-semibold">Total Revenue</th>
                        <th className="text-right py-3 px-3 text-gray-600 font-semibold">Profit</th>
                        <th className="text-right py-3 px-4 text-gray-600 font-semibold">Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.data.map((row, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <p className="font-medium text-gray-800">{row.product_name}</p>
                            <p className="text-xs font-mono text-gray-400">{row.master_sku}</p>
                          </td>
                          {/* Shopify */}
                          <td className="py-3 px-3 text-right text-green-700">{fmtN(row.shopify.units)}</td>
                          <td className="py-3 px-3 text-right text-green-700">{fmt(row.shopify.revenue)}</td>
                          {/* eBay */}
                          <td className="py-3 px-3 text-right text-blue-700">{fmtN(row.ebay.units)}</td>
                          <td className="py-3 px-3 text-right text-blue-700">{fmt(row.ebay.revenue)}</td>
                          {/* Amazon */}
                          <td className="py-3 px-3 text-right text-orange-700">{fmtN(row.amazon.units)}</td>
                          <td className="py-3 px-3 text-right text-orange-700">{fmt(row.amazon.revenue)}</td>
                          {/* Totals */}
                          <td className="py-3 px-3 text-right font-semibold text-gray-800 border-l border-gray-100">{fmtN(row.total_units)}</td>
                          <td className="py-3 px-3 text-right font-semibold text-gray-800">{fmt(row.total_revenue)}</td>
                          <td className="py-3 px-3 text-right font-bold text-green-600">{fmt(row.total_profit)}</td>
                          <td className="py-3 px-4 text-right">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              parseFloat(row.profit_margin) > 20 ? 'bg-green-100 text-green-700' :
                              parseFloat(row.profit_margin) > 0  ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {row.profit_margin}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {reportData.data.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    No sales found for mapped products in this date range.
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Modals */}
      {showCreate && (
        <CreateProductModal platform={platform} cfg={cfg} stores={stores} onClose={() => setShowCreate(false)} onSaved={fetchProducts} />
      )}
      {addMappingFor && (
        <AddMappingModal
          platform={platform}
          product={addMappingFor}
          stores={stores}
          onClose={() => setAddMappingFor(null)}
          onSaved={fetchProducts}
        />
      )}
    </div>
  );
}

export default SkuMapping;